import { Component } from 'react';

import { VerticalContainer, Wrapper } from '@american-autonomy/ag-ui';

import PrimaryButton from 'components/buttons/PrimaryButton';

import useTranslation from './hooks/useTranslation';

import { TEST_ID } from './constants';
import type { ErrorInfo, FallbackProps, FunctionComponent, Props, State } from './types';

import styles from './styles.module.scss';

// Class component required: React's error-boundary API (componentDidCatch /
// getDerivedStateFromError) has no function-component equivalent.
class ErrorBoundary extends Component<Props, State> {
    state: State = { hasError: false };

    static getDerivedStateFromError(_error: Error): State {
        return { hasError: true };
    }

    componentDidCatch(error: Error, info: ErrorInfo): void {
        console.error('ErrorBoundary caught error:', error, info.componentStack);
    }

    render() {
        const { children, className = '', testId = TEST_ID } = this.props;

        if (this.state.hasError) {
            return <ErrorBoundaryFallback className={className} testId={testId} />;
        }

        return children;
    }
}

const handleReload = (): void => window.location.reload();

const ErrorBoundaryFallback: FunctionComponent<FallbackProps> = ({ className = '', testId }) => {
    const { MAP_ERROR_MESSAGE, MAP_FAILED_TO_LOAD, RELOAD_BUTTON } = useTranslation();

    return (
        <VerticalContainer className={`${styles.errorBoundary} ${className}`} testId={testId}>
            <Wrapper>{MAP_FAILED_TO_LOAD}</Wrapper>

            <Wrapper>{MAP_ERROR_MESSAGE}</Wrapper>

            <PrimaryButton onClick={handleReload} text={RELOAD_BUTTON} />
        </VerticalContainer>
    );
};

export default ErrorBoundary;
